// Lib
import React, { ReactElement } from 'react';
import { useSelector } from 'react-redux';

// Utils
import { getIsSupportedUserAgent } from '../utils/platformBrowserSupportUtils';

// Selectors
import { getPlatformDetailsSelector } from '../platformSelector';

// Components
import PlatformCookieFullScreenWarning from './PlatformCookieFullScreenWarning';
import PlatformUnsupportedFullScreenWarning from './PlatformUnsupportedFullScreenWarning';

// Hooks
import useLocalStorage from '../../utils/services/localStorage/useLocalStorage';

// Types
import { PlatformDetails } from '../../../common/platform/platformTypes';

const BROWSER_UNSUPPORTED_ACKNOWLEDGEMENT_KEY = 'unsupportedAcknowledgement';

/**
 * Determines whether the browser is supported and also has the features required to run Milanote smoothly.
 */
const shouldShowUnsupportedBrowserWarning = (platformDetails: PlatformDetails): boolean => {
    if (!getIsSupportedUserAgent(platformDetails.userAgent)) return true;
    if (!platformDetails.features.supportsSvg) return true;
    if (!platformDetails.features.supportsContentEditable) return true;
    if (!platformDetails.features.supportsWebSockets) return true;

    return false;
};

/**
 * This component is responsible for checking if the user's platform
 */
const PlatformSupportManager = (): ReactElement | null => {
    const [userHasAcknowledged, setUserHasAcknowledged] = useLocalStorage(
        BROWSER_UNSUPPORTED_ACKNOWLEDGEMENT_KEY,
        false,
    );

    const acknowledgeUnsupportedBrowser = () => setUserHasAcknowledged(true);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    // if user does not have local storage enabled
    if (!platformDetails.features.supportsLocalStorage) return <PlatformCookieFullScreenWarning />;

    // if user is not using a supported browser, or their browser doesn't support required features
    if (shouldShowUnsupportedBrowserWarning(platformDetails) && !userHasAcknowledged) {
        return <PlatformUnsupportedFullScreenWarning acknowledgeUnsupportedBrowser={acknowledgeUnsupportedBrowser} />;
    }

    return null;
};

export default PlatformSupportManager;
